import type { CompanyResponseDto } from '@/services/generated/api';

/**
 * Where along the road the tenant is. It is what decides where to send them on
 * entering, which menu destinations do not apply yet, and what the «Empezar
 * aquí» screen shows.
 *
 * ## Why there are THREE phases and not the wizard's four steps
 *
 * The onboarding wizard (view 13) has four steps, and it would be better to say
 * "you are on 2 of 4". That is not possible, and the reason is concrete: the
 * wizard's progress lives in `useState` and **is persisted nowhere**. The
 * endpoint that would give the detail, `onboardingControllerStatus`, is
 * `GET /api/certification-runs/{id}` — it needs the run's id, and there is no
 * listing to discover it (gap **D5** of the screen inventory). On a fresh load
 * the app has no way of knowing which step it was left on.
 *
 * So the phase is derived from the only durable thing that does exist today:
 * `GET /api/companies`. It is coarser than the four steps, but it is true — and
 * that matters more, because a wrong progress indicator is worse than a coarse
 * one.
 *
 * Once D5 exists, this is refined without touching the consumers: the phase is
 * still the same question, only better answered.
 */
export type OnboardingPhase = 'no-company' | 'in-certification' | 'operating';

/**
 * `isProduction` is the signal that a company is already authorised to issue
 * before the SII: it is host geography (maullín vs palena) and is only set to
 * `true` once the company has moved to producción. With at least one company in
 * producción, the tenant is operating.
 *
 * With no companies there is no possible ambiguity: it has not started.
 */
export function deriveOnboardingPhase(
  companies: readonly CompanyResponseDto[] | undefined,
): OnboardingPhase {
  // `undefined` is "the response has not arrived yet", not "there are no
  // companies". It is deliberately treated as the furthest phase: that way the
  // menu does not flicker showing locks while loading, nor does the redirect fire
  // over data that does not exist yet. The consumers check `isLoading` separately.
  if (companies === undefined) return 'operating';
  if (companies.length === 0) return 'no-company';
  return companies.some((company) => company.isProduction) ? 'operating' : 'in-certification';
}

/**
 * The minimum phase a destination needs to make sense. It is the replacement for
 * hiding: a destination that does not apply yet is shown with a lock and with the
 * reason, because an absent item teaches nothing — and because the product is
 * sold saying the ERP can be integrated WHILE certifying, so «Integraciones»
 * cannot disappear.
 */
export const PHASE_RANK: Readonly<Record<OnboardingPhase, number>> = {
  'no-company': 0,
  'in-certification': 1,
  operating: 2,
};

/** `true` when the current phase reaches the minimum the destination asks for. */
export function phaseReaches(actual: OnboardingPhase, minima: OnboardingPhase): boolean {
  return PHASE_RANK[actual] >= PHASE_RANK[minima];
}
